import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Typography,
  useTheme,
} from "@mui/material";
import DataTable from "../../components/DataTable";
import { motion } from "framer-motion";
import {
  ClientResponse,
  Product,
  ProductResponse,
  User,
  UserResponse,
} from "../../types/api.types";
import SyncIcon from "@mui/icons-material/Sync";
import { productService } from "../../services/product/productService";
import { base64ToImage } from "../../utils/image";

const ProductPage = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [currentPage, setCurrentPage] = useState(0);

  const [products, setProducts] = useState<ProductResponse | null>(null);

  const [selectedProductImageToView, setSelectedProductImageToView] =
    useState<Product | null>(null);

  const theme = useTheme();

  // Imagen simple SVG - Cuadrado verde con texto "P"
  const TEST_IMAGE_URL =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='50' height='50'%3E%3Crect width='50' height='50' fill='%234CAF50'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='white' font-size='24' font-family='Arial'%3EP%3C/text%3E%3C/svg%3E";
  const columns = [
    {
      header: "",
      key: "picture",
      render: (value: any) => {
        // Si viene del backend, usar value?.picture, sino usar la imagen de prueba
        const imageUrl = TEST_IMAGE_URL;

        return (
          <Box
            sx={{
              width: 50,
              height: 50,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              // backgroundColor: "#f5f5f5",
              cursor: "pointer",
            }}
            onClick={() => setSelectedProductImageToView(value)}
          >
            <img
              src={imageUrl}
              alt="Product"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: 4,
                border: "1px solid #e0e0e0",
              }}
              onError={(e) => {
                console.error("Error loading image");
                e.currentTarget.src = TEST_IMAGE_URL;
              }}
            />
          </Box>
        );
      },
    },
    {
      header: "Código",
      key: "code",
    },
    {
      header: "Descripción",
      key: "description",
    },
    {
      header: "Estado",
      key: "visible",
      render: (value: any) => {
        return (
          <Chip
            label={value?.visible ? "Activo" : "Inactivo"}
            size="small"
            sx={{
              backgroundColor: value?.visible
                ? theme.palette.primary.main + "20"
                : theme.palette.error.main + "20",
              color: value?.visible
                ? theme.palette.primary.main
                : theme.palette.error.main,
              fontWeight: "600",
              border: `1px solid ${
                value?.visible
                  ? theme.palette.primary.main + "40"
                  : theme.palette.error.main + "40"
              }`,
              borderRadius: 2,
            }}
          />
        );
      },
    },
  ];

  const getProducts = async () => {
    setIsLoading(true);
    const response = await productService.getProducts(currentPage, 10);
    setProducts(response);
    setIsLoading(false);
  };

  const onSyncProducts = async () => {
    setIsLoading(true);
    await productService.syncProducts();
    await getProducts();
    setIsLoading(false);
  };

  useEffect(() => {
    getProducts();
  }, [currentPage]);

  return (
    <Box>
      <Card>
        <CardContent>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 10,
            }}
          >
            <Typography variant="h4">Productos</Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SyncIcon />}
                onClick={onSyncProducts}
                disabled={isLoading}
                loading={isLoading}
              >
                Sincronizar Productos
              </Button>
            </Box>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 2 }}
            style={{ marginTop: 2 }}
          >
            <DataTable
              isLoading={isLoading}
              columns={columns}
              total={products?.total || 0}
              data={products?.products || []}
              page={currentPage + 1}
              onChange={(page) => {
                setCurrentPage(page - 1);
              }}
            />
          </motion.div>
        </CardContent>
      </Card>

      <Dialog
        open={!!selectedProductImageToView}
        onClose={() => setSelectedProductImageToView(null)}
      >
        <DialogTitle>
          Imagen del Producto{" "}
          <strong>{selectedProductImageToView?.code}</strong>
        </DialogTitle>
        <DialogContent>
          <img
            style={{ width: "500px", height: "500px", objectFit: "cover" }}
            // src={selectedProductImageToView?.picture || ""}
            src={TEST_IMAGE_URL}
            alt="Product"
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ProductPage;
